import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useAbpPermissions } from '../contexts/AbpPermissionsContext';
import { UserRole } from '../types/roles';
import { useTranslation } from '../i18n/useTranslation';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Required role(s) to access this route
   * If array, user must have at least ONE of the roles
   * @deprecated Use requirePermission with ABP permissions instead
   */
  requireRole?: UserRole | UserRole[];
  /**
   * Required ABP permission(s) to access this route
   * If array, user must have at least ONE of the permissions
   * Use AbpPermissions constants for type safety
   */
  requirePermission?: string | string[];
  /**
   * If true, requires ALL specified roles (instead of ANY)
   * @deprecated Use requireAllPermissions instead
   */
  requireAllRoles?: boolean;
  /**
   * If true, requires ALL specified permissions (instead of ANY)
   */
  requireAllPermissions?: boolean;
  /**
   * Custom unauthorized component
   */
  unauthorizedComponent?: React.ReactNode;
}

/**
 * Protected route wrapper with ABP permission-based access control
 * - Requires authentication (redirects to login if not authenticated)
 * - Optionally requires specific ABP permission(s)
 * - Shows unauthorized message if user lacks required access
 * - Mobile-friendly: uses redirect flow (not popups)
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireRole,
  requirePermission,
  requireAllRoles = false,
  requireAllPermissions = false,
  unauthorizedComponent,
}) => {
  const auth = useAuth();
  const { t } = useTranslation();
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading: permissionsLoading } = useAbpPermissions();

  // Trigger login redirect in useEffect to avoid setState during render
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      void auth.signinRedirect();
    }
  }, [auth.isLoading, auth.isAuthenticated, auth]);

  // Show loading state while checking authentication or permissions
  if (auth.isLoading || permissionsLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" aria-hidden="true"></div>
          <p className="text-neutral-600">
            {auth.isLoading ? t('App.Auth.CheckingAuth') : t('App.Auth.LoadingPermissions')}
          </p>
        </div>
      </div>
    );
  }

  // If not authenticated, show redirecting message
  if (!auth.isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">{t('App.Auth.RedirectingToLogin')}</p>
        </div>
      </div>
    );
  }

  // Check role-based access (DEPRECATED - use requirePermission instead)
  if (requireRole) {
    console.warn('[ProtectedRoute] requireRole is deprecated. Use requirePermission with ABP permissions instead.');

    // For backwards compatibility, check if user has the role in OIDC token
    const roles = auth.user?.profile?.role;
    const userRoles = Array.isArray(roles) ? roles : roles ? [roles] : [];
    const requiredRoles = Array.isArray(requireRole) ? requireRole : [requireRole];

    const hasRequiredRole = requireAllRoles
      ? requiredRoles.every(role => userRoles.includes(role))
      : requiredRoles.some(role => userRoles.includes(role));

    if (!hasRequiredRole) {
      return unauthorizedComponent || (
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">🔒</div>
            <h1 className="text-2xl font-bold text-neutral-800 mb-2">{t('App.Auth.AccessDenied')}</h1>
            <p className="text-neutral-600 mb-6">
              {t('App.Auth.NoRoleAccess')}
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px]"
            >
              {t('App.Common.GoBack')}
            </button>
          </div>
        </div>
      );
    }
  }

  // Check ABP permission-based access
  if (requirePermission) {
    const permissions = Array.isArray(requirePermission) ? requirePermission : [requirePermission];

    const hasRequiredPermission = requireAllPermissions
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasRequiredPermission) {
      return unauthorizedComponent || (
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">🔒</div>
            <h1 className="text-2xl font-bold text-neutral-800 mb-2">{t('App.Auth.AccessDenied')}</h1>
            <p className="text-neutral-600 mb-6">
              {t('App.Auth.NoPermissionAccess')}
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px]"
            >
              {t('App.Common.GoBack')}
            </button>
          </div>
        </div>
      );
    }
  }

  // User is authenticated and has required access, render children
  return <>{children}</>;
};
